import { showComparisonEnrichedSchema, showProductsEnrichedSchema, type AppConfig, type Target } from "@ai-guidance/shared";
import { resolveNavigatePath, resolveTarget } from "./resolve";
import { highlightElement } from "./guide";
import type { CommerceRuntime } from "./commerce/runtime";
import type { SdkAction } from "./commerce/wire";
import type { CapturedSnapshot } from "./snapshot";
import type { ChatSurface } from "./chatSurface";
import { chromeCopy } from "./uiText";

/**
 * Execută secvențial lista de acțiuni primită de la agent. Fiecare acțiune
 * e fail-safe: o țintă lipsă produce un mesaj în widget, nu o excepție care
 * să rupă aplicația clientului.
 */
export class ActionExecutor {
  /** Snapshot-ul trimis cu întrebarea curentă - harta prin care se rezolvă `idx`. */
  private captured: CapturedSnapshot | null = null;

  constructor(
    private readonly config: AppConfig,
    private readonly widget: ChatSurface,
    /** Telemetrie: ținta unei acțiuni nu s-a găsit pe pagină (link mort,
     *  selector învechit) - semnal de calitate a ghidării, fire-and-forget. */
    private readonly onTargetMissing?: () => void,
  ) {}

  /**
   * Partea de commerce, DOAR pe appuri cu `commerce` în config (adaptor +
   * carduri + coș). Setter, nu parametru de constructor, fiindcă runtime-ul are
   * nevoie de shadow root-ul widgetului - deci se naște după `widget.mount()`,
   * iar executorul există dinainte. Cât timp lipsește, acțiunile de coș se
   * ignoră în tăcere: pe un sit obișnuit n-avem cu ce le executa.
   */
  setCommerce(commerce: CommerceRuntime): void {
    this.commerce = commerce;
  }

  private commerce?: CommerceRuntime;

  /**
   * Ce facem cu conversația SCRISĂ chiar înainte ca o acțiune să ducă pagina
   * altundeva (azi: `open_search`). Se armează per tură, ca `setSnapshot` -
   * intenția de continuare are nevoie de întrebarea ȘI de numărul de hop-uri ale
   * turei CURENTE, iar pe voce nu se armează deloc (vocea are propriul protocol
   * în doi timpi, iar un pending orfan ar declanșa o întrebare nedorită la
   * următoarea încărcare de pagină din tab).
   */
  private onPageChange: (() => void) | undefined;

  armPageChange(fn: (() => void) | null): void {
    this.onPageChange = fn ?? undefined;
  }

  /**
   * Chemat imediat ÎNAINTE de o navigare care DESCARCĂ documentul (`location.assign`),
   * indiferent ce acțiune a produs-o. Distinct de `armPageChange`:
   *
   * - se armează o singură dată, la init, nu per tură - vocea poate porni la
   *   mijlocul conversației scrise, iar saltul poate veni de la orice acțiune;
   * - NU se cheamă pe navigarea prin hash, unde documentul supraviețuiește și
   *   conversația vocală merge mai departe fără să știe nimic.
   *
   * Există pentru voce: e ultima fereastră în care mai putem lăsa un semn în
   * `sessionStorage` că pe pagina nouă trebuie reluată conversația.
   */
  private onDocumentUnloadNav: (() => void) | undefined;

  onReloadNavigation(fn: (() => void) | null): void {
    this.onDocumentUnloadNav = fn ?? undefined;
  }

  /**
   * Legat de întrebare, nu de pagină: acțiunile unui răspuns se rezolvă prin
   * snapshot-ul trimis CU acea întrebare, nu prin altul mai nou.
   */
  setSnapshot(captured: CapturedSnapshot | null): void {
    this.captured = captured;
  }

  async run(actions: SdkAction[]): Promise<void> {
    for (const action of actions) {
      try {
        await this.runOne(action);
      } catch (err) {
        console.warn("[ai-guidance] action failed", action.type, err);
      }
    }
  }

  private async runOne(action: SdkAction): Promise<void> {
    switch (action.type) {
      case "highlight": {
        const el = resolveTarget(action.target, this.config, this.captured);
        if (!el) return this.targetMissing();
        highlightElement(el, action.title, action.description);
        return;
      }
      case "tooltip": {
        const el = resolveTarget(action.target, this.config, this.captured);
        if (!el) return this.targetMissing();
        highlightElement(el, undefined, action.text);
        return;
      }
      case "scrollTo": {
        const el = resolveTarget(action.target, this.config, this.captured);
        if (!el) return this.targetMissing();
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      case "waitForElement":
        return this.waitForElement(action.target, action.timeoutMs);
      case "askUser":
        this.widget.addMessage("agent", action.question);
        this.widget.open();
        return;
      case "navigate": {
        // Emis de calea vocală și, din 27 aug 2026, de calea TEXT pe un app
        // commerce (chatul nu mai randează carduri - „îți arăt" = te duc acolo).
        // Calea cerută de model se ANCOREAZĂ într-o pagină din configul
        // publicat - prima probă reală a cerut „/pipeline" pentru
        // „/orbit-crm/#/pipeline", iar saltul orb a însemnat reload pe 404 și o
        // conversație moartă. Nicio potrivire → mesajul de țintă lipsă, nu o
        // navigare inventată.
        // Pe un magazin (adaptorul de commerce e prezent) se acceptă în plus
        // orice cale same-origin - pagină de produs sau de categorie. Că pagina
        // EXISTĂ a verificat deja backendul, în catalog (navigateAllowed).
        const dest = resolveNavigatePath(action.path, this.config, { commerce: this.commerce !== undefined });
        if (!dest) return this.targetMissing();
        // `onPageChange` armează continuarea conversației SCRISE înainte ca
        // documentul să se descarce (același mecanism ca la `open_search`); pe
        // voce nu e armat niciodată, deci acolo saltul rămâne exact ca înainte.
        this.goTo(dest, this.onPageChange);
        return;
      }
      case "show_products": {
        // Forma pe care o randăm e cea ÎMBOGĂȚITĂ de backend din catalog
        // (titlu, preț, imagine) - `showProductsActionSchema`, ce emite modelul,
        // e doar o listă de id-uri. Un backend care n-a îmbogățit (rollback) nu
        // trebuie să scoată carduri goale: fără potrivire, nu se randează nimic.
        const enriched = showProductsEnrichedSchema.safeParse(action);
        if (!enriched.success) return;
        this.commerce?.showProducts(enriched.data.items);
        return;
      }
      case "show_comparison": {
        // Aceeași grijă ca la `show_products`: forma randabilă e cea ÎMBOGĂȚITĂ
        // de backend. Un backend vechi (rollback) n-are cum s-o trimită, iar un
        // payload care nu se potrivește nu produce un tabel pe jumătate gol - nu
        // produce nimic.
        const comparison = showComparisonEnrichedSchema.safeParse(action);
        if (!comparison.success) return;
        this.commerce?.showComparison(comparison.data);
        return;
      }
      case "add_to_cart":
        // Acțiunile de coș sunt same-page (AJAX): nu declanșează nicio
        // continuare și nicio reluare de tură - vocea vorbește mai departe
        // singură, iar rezultatul ajunge la agent prin `snapshot.cart`.
        await this.commerce?.addToCart(action.variantId, action.quantity);
        return;
      case "set_cart_quantity":
        await this.commerce?.setQuantity(action.variantId, action.quantity);
        return;
      case "open_search": {
        // Explorare, nu recomandare: rezultatele apar în grila NATIVĂ a temei,
        // deci vizitatorul vede tot ce are magazinul, nu trei carduri alese de
        // noi. Calea o compune adaptorul din text (`searchPath`) - modelul n-are
        // cum să ne trimită în altă parte. Restul e IDENTIC cu `open_checkout`:
        // aceeași navigare, iar schimbarea de path declanșează singură protocolul
        // în doi timpi al vocii.
        //
        // Bugetul rostit („până în cinci sute de lei") intră în ACEEAȘI cale, ca
        // filtru real al magazinului - pagina de rezultate sosește deja
        // strâmtată. E singura formă de filtrare pe care o putem executa: bara
        // de filtre a temei e markup de client, iar promptul interzice explicit
        // să pretindem că o atingem (buclă live, 9 aug 2026: „deschid acum
        // filtrele de preț", de două ori, fără să existe acțiunea).
        const path = this.commerce?.searchPath(action.query, {
          minPrice: action.minPrice,
          maxPrice: action.maxPrice,
        });
        if (!path) return;
        const url = new URL(path, location.href);
        // Pe calea scrisă, navigarea descarcă documentul - deci conversația
        // trebuie ARMATĂ înainte de salt, altfel omul ajunge pe rezultate cu un
        // panou care nu mai are ce spune. Vezi `armPageChange` (sdk.ts).
        this.goTo(
          {
            pathname: url.pathname,
            search: url.search,
            hash: url.hash,
            full: url.pathname + url.search + url.hash,
          },
          this.onPageChange,
        );
        return;
      }
      case "open_checkout": {
        // Calea vine din configul appului (`commerce.cartPath`), nu de la model,
        // deci n-are nevoie de ancorarea în pagini scanate ca `navigate`. Restul
        // e ACELAȘI mecanism: schimbarea de path e ce declanșează, natural,
        // protocolul în doi timpi al vocii (voice/controller.ts → pollState).
        const cartPath = this.commerce?.cartPath();
        if (!cartPath) return;
        const url = new URL(cartPath, location.href);
        this.goTo({ pathname: url.pathname, hash: url.hash, full: url.pathname + url.hash });
        return;
      }
      case "completeTask":
        if (action.summary) this.widget.addMessage("agent", action.summary);
        return;
    }
  }

  /** Saltul propriu-zis, comun lui `navigate`, `open_checkout` și `open_search`. */
  private goTo(
    dest: { pathname: string; search?: string; hash: string; full: string },
    beforeNavigate?: (() => void) | undefined,
  ): void {
    // Query-ul intră în identitatea destinației DOAR de la `open_search`
    // încoace: două căutări diferite au același pathname (`/search`), deci fără
    // el a doua cerere („acum arată-mi burghie") ar fi citită ca „ești deja
    // acolo" și n-ar naviga niciodată. Pentru `navigate`/`open_checkout` e gol
    // de ambele părți, deci comparația rămâne exact cea de până acum.
    const search = dest.search ?? "";
    const hereSearch = location.search || "";
    const here = location.pathname + hereSearch + location.hash;
    if (
      dest.full === here ||
      `${trimSlash(dest.pathname)}${search}${dest.hash}` ===
        `${trimSlash(location.pathname)}${hereSearch}${location.hash}`
    )
      return;
    // Saltul CHIAR se întâmplă: abia acum are voie cel care ne-a dat acțiunea
    // să-și scrie intenția de continuare. Chemat înainte de mutarea locației,
    // fiindcă `location.assign` poate descărca documentul imediat după.
    beforeNavigate?.();
    // Harta idx → nod e a paginii VECHI: o invalidăm înainte de salt, ca un
    // highlight din aceeași tură să cadă pe elementId/fallbackText (config),
    // nu pe un idx care acum arată alt element.
    this.setSnapshot(null);
    if (dest.hash && trimSlash(dest.pathname) === trimSlash(location.pathname)) {
      // Același document, altă rută de hash: schimbăm DOAR hash-ul - fără
      // reload, query-ul rămâne, iar conversația vocală nu se întrerupe.
      location.hash = dest.hash;
    } else {
      // Ultimul lucru dinaintea descărcării documentului: aici își lasă vocea
      // markerul de reconectare. Un eșec aici n-are voie să blocheze saltul.
      try {
        this.onDocumentUnloadNav?.();
      } catch (err) {
        console.warn("[ai-guidance] pre-navigation hook failed", err);
      }
      location.assign(dest.full);
    }
  }

  private waitForElement(target: Target, timeoutMs: number): Promise<void> {
    return new Promise((resolve) => {
      const startedAt = performance.now();
      const tick = () => {
        if (resolveTarget(target, this.config, this.captured)) return resolve();
        if (performance.now() - startedAt >= timeoutMs) {
          this.targetMissing();
          return resolve();
        }
        setTimeout(tick, 200);
      };
      tick();
    });
  }

  private targetMissing(): void {
    this.onTargetMissing?.();
    this.widget.addMessage("agent", chromeCopy(this.config.business?.language).targetMissing);
    this.widget.open();
  }
}

/** Identitate de pathname insensibilă la slash-ul final: „/a/" și „/a" sunt aceeași pagină. */
function trimSlash(pathname: string): string {
  return pathname.replace(/\/+$/, "");
}
